<template>
  <b-modal
    v-model="localVisible"
    size="xl"
    title=""
    hide-footer
    centered
    hide-header-close
    @hidden="$emit('update:visible', false)"
    modal-class="vehicle-premium-modal"
    body-class="p-0"
    header-class="p-0 border-0"
  >
    <template #modal-header="{ close }">
      <div class="dashboard-header w-100 px-4 py-4 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
            <div class="vehicle-avatar shadow-lg mr-3">
                <i class="fa fa-truck"></i>
                <div class="status-indicator" :class="masterData.truckReachedStatus ? 'active' : 'inactive'"></div>
            </div>
            <div class="vehicle-identity">
                <div class="d-flex align-items-center">
                    <h3 class="mb-0 mr-3 font-weight-bold tracking-tight text-white">{{ vehicleNo }}</h3>
                    <b-badge variant="primary" class="premium-badge px-3 py-1 text-uppercase text-xs" v-if="masterData.vehicletypemaster_relation">
                        {{ masterData.vehicletypemaster_relation.vehicleType }}
                    </b-badge>
                </div>
                <div class="d-flex align-items-center mt-1">
                    <span class="text-white-50 text-xs mr-3"><i class="fa fa-barcode mr-1 opacity-50"></i> Chassis: <strong class="text-white">{{ masterData.chassisNo || 'N/A' }}</strong></span>
                    <span class="text-white-50 text-xs"><i class="fa fa-cog mr-1 opacity-50"></i> Model: <strong class="text-white">{{ masterData.modNo || 'N/A' }}</strong></span>
                </div>
            </div>
        </div>
        <div class="header-right-actions d-flex align-items-center">
            <div class="header-stats d-none d-lg-flex mr-4">
                <div class="stat-group stat-group-clickable" @click="activeTab = 'challan'">
                    <span class="stat-label">Challans</span>
                    <span class="stat-value" :class="challans.length > 0 ? 'warning' : 'success'">{{ challans.length }}</span>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-group stat-group-clickable" @click="activeTab = 'fasttag'">
                    <span class="stat-label">Activity</span>
                    <span class="stat-value info">{{ fasttagData.length }}</span>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-group stat-group-clickable" @click="activeTab = 'maintenance'">
                    <span class="stat-label">Maintenance</span>
                    <span class="stat-value" :class="maintenanceHistory.length > 0 ? 'info' : 'success'">{{ maintenanceHistory.length }}</span>
                </div>
            </div>
            <button type="button" class="premium-close-btn" @click="localVisible = false">
                <i class="fa fa-times"></i>
            </button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="loader-container">
      <div class="premium-loader"></div>
      <p class="mt-4 font-weight-bold text-muted">Analyzing Fleet Intelligence...</p>
    </div>

    <div v-else-if="error" class="error-container p-5 text-center">
        <div class="error-icon mb-3">
            <i class="fa fa-exclamation-circle text-danger fa-3x"></i>
        </div>
        <h5 class="text-danger">Syncing Failed</h5>
        <p class="text-muted">{{ error }}</p>
        <b-button variant="outline-danger" pill size="sm" @click="fetchDetails">Retry Synchronization</b-button>
    </div>

    <div v-else class="modal-dashboard-layout">
      <!-- SIDE NAVIGATION -->
      <div class="dashboard-sidebar">
          <div 
            v-for="tab in tabs" 
            :key="tab.id"
            class="nav-item-premium"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
              <i :class="tab.icon"></i>
              <span>{{ tab.label }}</span>
              <b-badge v-if="tab.count !== undefined" pill variant="primary" class="ml-auto ml-2">{{ tab.count }}</b-badge>
          </div>
      </div>

      <!-- MAIN CONTENT AREA -->
      <div class="dashboard-main-content p-4">
          <!-- OVERVIEW / MASTER TAB -->
          <div v-if="activeTab === 'master'" class="fade-in">
              <div class="row">
                  <div class="col-md-7">
                      <div class="glass-card mb-4 p-4 h-100">
                          <h6 class="card-subtitle-premium"><i class="fa fa-id-card-o mr-2"></i> Identification & Specifications</h6>
                          <div class="row mt-4">
                              <div class="col-sm-6 mb-4">
                                  <label class="info-label">Registration Number</label>
                                  <div class="info-value-premium">{{ masterData.regdNo || 'N/A' }}</div>
                              </div>
                              <div class="col-sm-6 mb-4">
                                  <label class="info-label">Model No / Year</label>
                                  <div class="info-value-premium">{{ masterData.modNo }} / {{ masterData.vmodelYear || 'N/A' }}</div>
                              </div>
                              <div class="col-sm-6 mb-4">
                                  <label class="info-label">Chassis Number</label>
                                  <div class="info-value-premium text-monospace small">{{ masterData.chassisNo || 'N/A' }}</div>
                              </div>
                              <div class="col-sm-6 mb-4">
                                  <label class="info-label">Engine Number</label>
                                  <div class="info-value-premium text-monospace small">{{ masterData.engineNo || 'N/A' }}</div>
                              </div>
                              <div class="col-sm-6">
                                  <label class="info-label">Last Login / Audit</label>
                                  <div class="info-value-premium small">{{ formatDateTime(masterData.loginTime) }}</div>
                              </div>
                              <div class="col-sm-6">
                                  <label class="info-label">Reach Status</label>
                                  <b-badge :variant="masterData.truckReachedStatus ? 'success' : 'secondary'" pill>
                                      {{ masterData.truckReachedStatus ? 'Reached' : 'In Transit' }}
                                  </b-badge>
                              </div>
                          </div>
                      </div>
                  </div>
                  <div class="col-md-5">
                      <div class="glass-card mb-4 compliance-card p-4 h-100">
                          <h6 class="card-subtitle-premium"><i class="fa fa-shield mr-2"></i> Compliance Track</h6>
                          <div class="compliance-list mt-3">
                              <!-- STATUS FLAGS -->
                              <div class="d-flex flex-wrap mb-3 border-bottom pb-3 opacity-90">
                                  <div class="mr-3 mb-2">
                                      <label class="text-xs text-muted d-block mb-1">Permit</label>
                                      <b-badge :variant="masterData.nPermit == 1 ? 'success' : 'danger'" pill class="dot-badge">{{ masterData.nPermit == 1 ? 'Valid' : 'Invalid' }}</b-badge>
                                  </div>
                                  <div class="mr-3 mb-2">
                                      <label class="text-xs text-muted d-block mb-1">MV Tax</label>
                                      <b-badge :variant="masterData.mvTax == 1 ? 'success' : 'danger'" pill class="dot-badge">{{ masterData.mvTax == 1 ? 'Paid' : 'Due' }}</b-badge>
                                  </div>
                                  <div class="mr-3 mb-2">
                                      <label class="text-xs text-muted d-block mb-1">Insurance</label>
                                      <b-badge :variant="masterData.insurValid == 1 ? 'success' : 'danger'" pill class="dot-badge">{{ masterData.insurValid == 1 ? 'Active' : 'Expired' }}</b-badge>
                                  </div>
                                  <div class="mr-3 mb-2">
                                      <label class="text-xs text-muted d-block mb-1">Fitness</label>
                                      <b-badge :variant="masterData.fitness == 1 ? 'success' : 'danger'" pill class="dot-badge">{{ masterData.fitness == 1 ? 'Fit' : 'Unfit' }}</b-badge>
                                  </div>
                                  <div class="mb-2">
                                      <label class="text-xs text-muted d-block mb-1">Pollution</label>
                                      <b-badge :variant="masterData.Pollution == 1 ? 'success' : 'danger'" pill class="dot-badge">{{ masterData.Pollution == 1 ? 'Valid' : 'Invalid' }}</b-badge>
                                  </div>
                              </div>

                              <!-- VALIDITY DATES -->
                              <div class="compliance-row">
                                  <span>Fitness Validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('fitness'))">{{ formatDate(getComplianceDate('fitness')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('fitness'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>Insurance Validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('insurance'))">{{ formatDate(getComplianceDate('insurance')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('insurance'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>RC Validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('rc'))">{{ formatDate(getComplianceDate('rc')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('rc'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>MV Tax Validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('tax'))">{{ formatDate(getComplianceDate('tax')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('tax'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>NP Authorization</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('npAuthorization'))">{{ formatDate(getComplianceDate('npAuthorization')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('npAuthorization'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>Permit validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('permit'))">{{ formatDate(getComplianceDate('permit')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('permit'))"></div>
                              </div>
                              <div class="compliance-row">
                                  <span>Pollution Validity</span>
                                  <span class="date mt-1" :class="getDateColorClass(getComplianceDate('pollution'))">{{ formatDate(getComplianceDate('pollution')) }}</span>
                                  <div class="progress-simple mt-1" :class="getValidityStatus(getComplianceDate('pollution'))"></div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>

              <!-- DOCUMENTS SECTION -->
              <div class="row mt-4">
                  <div class="col-12">
                      <div class="glass-card mb-4 p-4">
                          <h6 class="card-subtitle-premium"><i class="fa fa-file-text-o mr-2"></i> Digital Archives</h6>
                          <div class="doc-grid mt-3">
                              <div v-for="doc in vehicleDocs" :key="doc.key" class="doc-card" @click="downloadDoc(doc.file, doc.label)">
                                  <div class="doc-icon">
                                      <i class="fa fa-file-pdf-o"></i>
                                  </div>
                                  <div class="doc-info">
                                      <span class="doc-name">{{ doc.label }}</span>
                                      <span class="doc-action text-primary">View / Download</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>

          <!-- RC / ULIP TAB -->
          <div v-if="activeTab === 'ulip'" class="fade-in">
              <div v-if="!ulipData.ulip_vehicle_id" class="no-data-msg p-5 text-center">
                  <i class="fa fa-database fa-3x mb-3 opacity-25"></i>
                  <h6>No ULIP Record Found</h6>
                  <p class="text-muted small">ULIP API has not yet synchronized for this registration number.</p>
              </div>
              <div v-else>
          <div v-if="activeTab === 'ulip'" class="fade-in">
              <div v-if="!ulipData.ulip_vehicle_id" class="no-data-msg p-5 text-center">
                  <i class="fa fa-database fa-3x mb-3 opacity-25"></i>
                  <h6>No ULIP Record Found</h6>
                  <p class="text-muted small">ULIP API has not yet synchronized for this registration number.</p>
              </div>
              <div v-else>
                  <!-- TOP SECTION: IDENTITY & TECH -->
                  <div class="row">
                      <div class="col-md-5">
                          <div class="stats-card owner-card mb-4 p-4 text-white shadow-lg h-100">
                              <div class="d-flex justify-content-between align-items-start">
                                  <div>
                                      <label class="text-white-50 text-xs font-weight-bold letter-spacing-1">OWNER {{(ulipData.rc_owner_sr || '1')}}</label>
                                      <h5 class="font-weight-bold mt-1 truncate-2">{{ ulipData.rc_owner_name }}</h5>
                                      <p class="text-white-50 small mb-0">{{ ulipData.rc_f_name ? 'S/O ' + ulipData.rc_f_name : '' }}</p>
                                  </div>
                                  <b-badge pill :variant="ulipData.rc_status === 'ACTIVE' ? 'success' : 'danger'" class="px-3 py-1">{{ ulipData.rc_status }}</b-badge>
                              </div>
                              <div class="row mt-4">
                                  <div class="col-6">
                                      <label class="info-label text-white-50">Regn No</label>
                                      <div class="font-weight-bold">{{ ulipData.rc_regn_no }}</div>
                                  </div>
                                  <div class="col-6">
                                      <label class="info-label text-white-50">Regn Date</label>
                                      <div class="font-weight-bold">{{ formatDate(ulipData.rc_regn_dt) }}</div>
                                  </div>
                              </div>
                              <div class="row mt-3">
                                  <div class="col-6">
                                      <label class="info-label text-white-50">Purchase Date</label>
                                      <div class="small">{{ formatDate(ulipData.rc_purchase_dt) }}</div>
                                  </div>
                                  <div class="col-6">
                                      <label class="info-label text-white-50">Regn Upto</label>
                                      <div class="small">{{ formatDate(ulipData.rc_regn_upto) }}</div>
                                  </div>
                              </div>
                              <div class="mt-4 pt-3 border-top border-white-10 d-flex justify-content-between align-items-center">
                                  <span class="text-white-50 small">ID: {{ ulipData.ulip_vehicle_id }}</span>
                                  <span v-if="ulipData.rc_non_use === 'true'" class="badge badge-warning">Non-Use Reported</span>
                              </div>
                          </div>
                      </div>
                      <div class="col-md-7">
                          <div class="glass-card mb-4 p-4 h-100">
                              <h6 class="card-subtitle-premium"><i class="fa fa-truck mr-2"></i> Technical Specifications</h6>
                              <div class="row mt-4">
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Maker / Model</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_maker_desc }}<br/>{{ ulipData.rc_maker_model }}</div>
                                  </div>
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Body / Class</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_body_type_desc }}<br/>{{ ulipData.rc_vch_catg }}</div>
                                  </div>
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Fuel / Norms</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_fuel_desc }}<br/>{{ ulipData.rc_norms_desc }}</div>
                                  </div>
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Weights (GVW/Unld)</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_gvw }} / {{ ulipData.rc_unld_wt }} KG</div>
                                  </div>
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Capacity (S/S/W)</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_seat_cap }}/{{ ulipData.rc_sleeper_cap || 0 }}/{{ ulipData.rc_wheelbase }}</div>
                                  </div>
                                  <div class="col-sm-4 mb-3">
                                      <label class="info-label">Engine / Cyl</label>
                                      <div class="info-value-premium small">{{ ulipData.rc_cubic_cap }} CC / {{ ulipData.rc_no_cyl }} Cyl</div>
                                  </div>
                                  <div class="col-12 mt-2">
                                      <div class="d-flex bg-light p-2 rounded justify-content-between">
                                          <span class="small text-muted">Chassis: {{ ulipData.rc_chasi_no }}</span>
                                          <span class="small text-muted">Engine: {{ ulipData.rc_eng_no }}</span>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>

                  <!-- MIDDLE SECTION: PERMIT & INSURANCE -->
                  <div class="row mt-2">
                      <div class="col-md-4">
                           <div class="glass-card mb-4 p-4">
                                <h6 class="card-subtitle-premium text-info"><i class="fa fa-file-text mr-2"></i> Permit Intelligence</h6>
                                <div class="info-item-row mt-3">
                                    <label>Permit No</label>
                                    <span class="small font-weight-bold">{{ ulipData.rc_permit_no || 'N/A' }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Type</label>
                                    <span class="small truncate">{{ ulipData.rc_permit_type || 'N/A' }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Valid From</label>
                                    <span class="small">{{ formatDate(ulipData.rc_permit_valid_from) }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Valid Upto</label>
                                    <span class="small font-weight-bold text-success">{{ formatDate(ulipData.rc_permit_valid_upto) }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Issued By</label>
                                    <span class="small truncate">{{ ulipData.rc_np_issued_by || 'N/A' }}</span>
                                </div>
                           </div>
                      </div>
                      <div class="col-md-4">
                           <div class="glass-card mb-4 p-4">
                                <h6 class="card-subtitle-premium text-primary"><i class="fa fa-shield mr-2"></i> Insurance Detail</h6>
                                <div class="info-item-row mt-3">
                                    <label>Company</label>
                                    <span class="small">{{ ulipData.rc_insurance_comp || 'N/A' }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Policy No</label>
                                    <span class="small truncate text-monospace">{{ ulipData.rc_insurance_policy_no || 'N/A' }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Valid Upto</label>
                                    <span class="small font-weight-bold">{{ formatDate(ulipData.rc_insurance_upto) }}</span>
                                </div>
                                <div class="mt-4 pt-3 border-top">
                                    <h6 class="card-subtitle-premium text-warning small"><i class="fa fa-university mr-2"></i> Finance Info</h6>
                                    <div class="info-item-row mt-2">
                                        <label>Financer</label>
                                        <span class="small truncate">{{ ulipData.rc_financer || 'N/A' }}</span>
                                    </div>
                                    <div class="info-item-row">
                                        <label>Sale Amt</label>
                                        <span class="small">₹{{ ulipData.rc_sale_amt || '0' }}</span>
                                    </div>
                                </div>
                           </div>
                      </div>
                      <div class="col-md-4">
                           <div class="glass-card mb-4 p-4">
                                <h6 class="card-subtitle-premium text-success"><i class="fa fa-check-circle mr-2"></i> Certificates</h6>
                                <div class="info-item-row mt-3">
                                    <label>Fitness Upto</label>
                                    <span class="small">{{ formatDate(ulipData.rc_fit_upto) }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>Tax Upto</label>
                                    <span class="small">{{ formatDate(ulipData.rc_tax_upto) }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>PUCC No</label>
                                    <span class="small truncate">{{ ulipData.rc_pucc_no || 'N/A' }}</span>
                                </div>
                                <div class="info-item-row">
                                    <label>PUCC Upto</label>
                                    <span class="small">{{ formatDate(ulipData.rc_pucc_upto) }}</span>
                                </div>
                                <div class="mt-4 pt-3 border-top">
                                    <label class="info-label">Color / Manufacturing</label>
                                    <div class="info-value-premium small">{{ ulipData.rc_color }} | {{ ulipData.rc_manu_month_yr }}</div>
                                </div>
                           </div>
                      </div>
                  </div>

                  <!-- BOTTOM SECTION: LOCATION & ADDRESS -->
                  <div class="row">
                      <div class="col-md-12">
                           <div class="glass-card p-4">
                              <h6 class="card-subtitle-premium"><i class="fa fa-map-marker mr-2"></i> Geolocation & Registry Intelligence</h6>
                              <div class="row mt-3">
                                  <div class="col-md-3">
                                      <label class="info-label">Registry Point</label>
                                      <div class="small font-weight-bold text-dark">{{ ulipData.rc_registered_at }}</div>
                                      <div class="text-xs text-muted">{{ ulipData.state_cd }} - {{ ulipData.rto_cd }}</div>
                                  </div>
                                  <div class="col-md-4">
                                      <label class="info-label">Present Address</label>
                                      <div class="small opacity-75">{{ ulipData.rc_present_address || 'N/A' }}</div>
                                  </div>
                                  <div class="col-md-5">
                                      <label class="info-label">Permanent Address</label>
                                      <div class="small opacity-75">{{ ulipData.rc_permanent_address || 'N/A' }}</div>
                                  </div>
                              </div>
                           </div>
                      </div>
                  </div>
              </div>
          </div>
              </div>
          </div>

          <!-- FASTAG TAB -->
          <div v-if="activeTab === 'fasttag'" class="fade-in h-100">
             <div v-if="!fasttagData.length" class="no-data-msg p-5 text-center">
                  <i class="fa fa-rss fa-3x mb-3 opacity-25"></i>
                  <h6>No Fasttag Activity</h6>
                  <p class="text-muted small">We haven't detected any toll crossings for this vehicle in the current billing cycle.</p>
              </div>
              <div v-else class="h-100 flex-column d-flex">
                   <div class="table-responsive-container flex-grow-1">
                        <table class="table premium-table">
                            <thead>
                                <tr>
                                    <th>Toll Plaza</th>
                                    <th>Direction</th>
                                    <th>Transaction Time</th>
                                    <th>Sequence No</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(log, idx) in fasttagData" :key="idx">
                                    <td class="font-weight-bold text-dark">{{ log.uf_toll_plaza_name }}</td>
                                    <td>
                                        <div class="direction-badge" :class="log.uf_lane_direction">
                                            <i class="fa" :class="log.uf_lane_direction === 'O' ? 'fa-arrow-left' : 'fa-arrow-right'"></i>
                                            {{ log.uf_lane_direction === 'O' ? 'Outbound' : 'Inbound' }}
                                        </div>
                                    </td>
                                    <td>{{ formatDateTime(log.uf_reader_read_time) }}</td>
                                    <td class="text-muted font-weight-normal">{{ log.uf_seq_no }}</td>
                                    <td><b-badge pill variant="light">{{ log.uf_vehicle_type }}</b-badge></td>
                                </tr>
                            </tbody>
                        </table>
                   </div>
              </div>
          </div>

          <!-- CHALLAN TAB -->
          <div v-if="activeTab === 'challan'" class="fade-in h-100">
             <div v-if="!challans.length" class="no-data-msg p-5 text-center">
                  <i class="fa fa-gavel fa-3x mb-3 opacity-25"></i>
                  <h6>Clean Legal History</h6>
                  <p class="text-muted small">No active or historical challans found on record for this registration.</p>
              </div>
              <div v-else class="h-100 flex-column d-flex">
                   <div class="table-responsive-container flex-grow-1">
                        <table class="table premium-table">
                            <thead>
                                <tr>
                                    <th>Challan details</th>
                                    <th>Status</th>
                                    <th>Fine Amount</th>
                                    <th>Location</th>
                                    <th>Offences</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(challan, idx) in challans" :key="idx">
                                    <td>
                                        <div class="d-flex flex-column">
                                            <span class="font-weight-bold">{{ challan.challan_no }}</span>
                                            <span class="text-xs text-muted">{{ formatDateTime(challan.challan_date_time) }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <b-badge :variant="challan.challan_status === 'Pending' ? 'danger' : 'success'" class="text-xs px-3 py-1">
                                            {{ challan.challan_status }}
                                        </b-badge>
                                    </td>
                                    <td>
                                        <div class="d-flex flex-column">
                                            <span class="font-weight-bold">₹{{ challan.received_amount || 0 }}</span>
                                            <span class="text-xs text-info" v-if="challan.fine_imposed">Imposed: ₹{{ challan.fine_imposed }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="max-w-200">
                                            <span class="small d-block text-truncate" v-b-tooltip.hover :title="challan.challan_place">
                                                {{ challan.challan_place }}
                                            </span>
                                        </div>
                                    </td>
                                    <td class="max-w-300">
                                        <div class="offence-tags">
                                            <span v-for="(off, oidx) in challan.offence_details" :key="oidx" class="offence-chip" v-b-tooltip.hover :title="off.act">
                                                {{ off.name }}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                   </div>
              </div>
          </div>

          <!-- MAINTENANCE TAB -->
          <div v-if="activeTab === 'maintenance'" class="fade-in h-100">
             <div v-if="maintenanceLoading" class="no-data-msg p-5 text-center">
                  <b-spinner variant="primary" class="mb-3"></b-spinner>
                  <h6>Loading Maintenance Details...</h6>
             </div>
             <div v-else class="h-100 flex-column d-flex">
                  <div v-if="maintenanceItemBrief.length" class="glass-card mb-4 p-4">
                      <h6 class="card-subtitle-premium text-primary"><i class="fa fa-wrench mr-2"></i> Maintenance Item Brief</h6>
                      <div class="table-responsive-container">
                          <table class="table premium-table mb-0">
                              <thead>
                                  <tr>
                                      <th>Item</th>
                                      <th>Total Qty</th>
                                      <th>Rate Range</th>
                                      <th>Total Spend</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  <tr v-for="(item, idx) in maintenanceItemBrief" :key="'brief-' + idx">
                                      <td class="font-weight-bold text-dark">{{ item.itemName || "N/A" }}</td>
                                      <td>{{ Number(item.totalQuantity || 0).toLocaleString("en-IN") }}</td>
                                      <td>₹{{ Number(item.minRate || 0).toLocaleString("en-IN") }} - ₹{{ Number(item.maxRate || 0).toLocaleString("en-IN") }}</td>
                                      <td class="font-weight-bold">₹{{ Number(item.totalSpend || 0).toLocaleString("en-IN") }}</td>
                                  </tr>
                              </tbody>
                          </table>
                      </div>
                  </div>
                  <div v-if="!maintenanceHistory.length" class="no-data-msg p-5 text-center">
                      <i class="fa fa-history fa-3x mb-3 opacity-25"></i>
                      <h6>No Maintenance Records Found</h6>
                      <p class="text-muted small">No maintenance tickets are available for this vehicle.</p>
                  </div>
                  <div v-else class="glass-card p-4">
                      <h6 class="card-subtitle-premium text-info"><i class="fa fa-list-alt mr-2"></i> Maintenance History</h6>
                      <div class="table-responsive-container">
                          <table class="table premium-table mb-0">
                              <thead>
                                  <tr>
                                      <th>Ticket</th>
                                      <th>Service Date</th>
                                      <th>Type</th>
                                      <th>Status</th>
                                      <th>Est. Cost</th>
                                      <th>Items</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  <tr v-for="(row, idx) in maintenanceHistory" :key="'maint-' + idx">
                                      <td class="font-weight-bold">{{ row.ticketId || row.id }}</td>
                                      <td>{{ formatDate(row.serviceDate) }}</td>
                                      <td>{{ row.serviceType || "N/A" }}</td>
                                      <td>
                                          <b-badge pill :variant="String(row.status || '').toLowerCase().includes('approve') ? 'success' : 'warning'">
                                              {{ row.status || "Pending" }}
                                          </b-badge>
                                      </td>
                                      <td class="font-weight-bold">₹{{ Number(row.estimatedCost || 0).toLocaleString("en-IN") }}</td>
                                      <td>{{ Array.isArray(row.items) ? row.items.length : 0 }}</td>
                                  </tr>
                              </tbody>
                          </table>
                      </div>
                  </div>
             </div>
          </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from "moment";
import VehiclePerformanceService from "../../../../services/VehiclePerformanceService";

export default {
  name: "VehicleMasterDetailsModal",
  props: {
    visible: { type: Boolean, default: false },
    vehicleTruckId: { type: [Number, String], default: null },
    vehicleNo: { type: String, default: "" }
  },
  data() {
    return {
      masterData: {},
      ulipData: {},
      challans: [],
      fasttagData: [],
      maintenanceHistory: [],
      maintenanceItemBrief: [],
      maintenanceLoading: false,
      loading: false,
      error: "",
      activeTab: "master",
      tabs: [
          { id: 'master', label: 'Fleet Master', icon: 'fa fa-th-large' },
          { id: 'ulip', label: 'RC Details', icon: 'fa fa-id-card' },
          { id: 'fasttag', label: 'Fasttag Activity', icon: 'fa fa-rss', count: 0 },
          { id: 'challan', label: 'Enforcements', icon: 'fa fa-gavel', count: 0 },
          { id: 'maintenance', label: 'Maintenance Details', icon: 'fa fa-wrench', count: 0 }
      ]
    };
  },
  computed: {
    localVisible: {
      get() { return this.visible; },
      set(v) { this.$emit("update:visible", v); }
    },
    vehicleDocs() {
        if (!this.masterData) return [];
        const docs = [];
        if (this.masterData.National_Permit_Authorization) docs.push({ label: 'NP Authorization', file: this.masterData.National_Permit_Authorization });
        if (this.masterData.National_Permit_PART_AB) docs.push({ label: 'NP Part A/B', file: this.masterData.National_Permit_PART_AB });
        if (this.masterData.FITNESS_CERTIFICATE) docs.push({ label: 'Fitness Cert', file: this.masterData.FITNESS_CERTIFICATE });
        if (this.masterData.INSURANCE) docs.push({ label: 'Insurance', file: this.masterData.INSURANCE });
        if (this.masterData.MU_TAX) docs.push({ label: 'Tax Receipt', file: this.masterData.MU_TAX });
        if (this.masterData.RC) docs.push({ label: 'Registration Cert', file: this.masterData.RC });
        if (this.masterData.pollutionCertificate) docs.push({ label: 'Pollution Cert', file: this.masterData.pollutionCertificate });
        if (this.masterData.uploadVehicleDoc) docs.push({ label: 'Other Document', file: this.masterData.uploadVehicleDoc });
        return docs;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.vehicleTruckId) {
        this.fetchDetails();
      }
    },
    activeTab(newTab) {
      if (newTab === "maintenance" && this.vehicleTruckId) {
        this.fetchMaintenanceDetails();
      }
    }
  },
  methods: {
    async fetchDetails() {
      this.loading = true;
      this.error = "";
      try {
        const res = await VehiclePerformanceService.getVehicleMasterDetails(this.vehicleTruckId);
        if (res.data && res.data.status) {
          const { master, ulip, challans, fasttag } = res.data.data;
          this.masterData = master || {};
          this.ulipData = ulip || {};
          this.challans = challans || [];
          this.fasttagData = fasttag || [];
          this.fetchMaintenanceDetails();

          // Update counts
          this.tabs.find(t => t.id === 'challan').count = this.challans.length;
          this.tabs.find(t => t.id === 'fasttag').count = this.fasttagData.length;
          this.tabs.find(t => t.id === 'maintenance').count = this.maintenanceHistory.length;
        } else {
          throw new Error(res.data.message || "Failed to load master details.");
        }
      } catch (err) {
        this.error = err.message;
        console.error("Master details fetch error:", err);
      } finally {
        this.loading = false;
      }
    },
    async fetchMaintenanceDetails() {
      if (!this.vehicleTruckId) return;
      this.maintenanceLoading = true;
      try {
        const params = { vehicleTruckId: this.vehicleTruckId };
        const [historyRes, briefRes] = await Promise.all([
          VehiclePerformanceService.getMaintenanceHistory(params),
          VehiclePerformanceService.getMaintenanceItemBrief(params)
        ]);
        this.maintenanceHistory =
          historyRes && historyRes.data && historyRes.data.status
            ? (historyRes.data.data || [])
            : [];
        this.maintenanceItemBrief =
          briefRes && briefRes.data && briefRes.data.status
            ? (briefRes.data.data || [])
            : [];
        const mTab = this.tabs.find(t => t.id === 'maintenance');
        if (mTab) mTab.count = this.maintenanceHistory.length;
      } catch (err) {
        console.error("Maintenance details fetch error:", err);
        this.maintenanceHistory = [];
        this.maintenanceItemBrief = [];
      } finally {
        this.maintenanceLoading = false;
      }
    },
    formatDate(date) {
        if (!date) return "N/A";
        return moment(date).format("DD-MMM-YYYY");
    },
    formatDateTime(date) {
        if (!date) return "N/A";
        return moment(date).format("DD-MMM-YYYY hh:mm A");
    },
    getValidityStatus(date) {
        if (!date) return "status-none";
        const diff = moment(date).diff(moment(), "days");
        if (diff < 0) return "status-danger";
        if (diff < 30) return "status-warning";
        return "status-success";
    },
    getDateColorClass(date) {
        if (!date) return "text-secondary";
        const diff = moment(date).diff(moment(), "days");
        if (diff < 0) return "text-danger font-weight-bold";
        if (diff < 30) return "text-warning font-weight-bold";
        return "text-success font-weight-bold";
    },
    getComplianceDate(type) {
        const dateMap = {
            fitness: [
                this.ulipData.rc_fit_upto,
                this.masterData.FCValidity
            ],
            insurance: [
                this.ulipData.rc_insurance_upto,
                this.masterData.INValidity
            ],
            rc: [
                this.ulipData.rc_regn_upto,
                this.masterData.RCValidity,
                this.masterData.rcValidity,
                this.ulipData.rc_regn_dt
            ],
            tax: [
                this.ulipData.rc_tax_upto,
                this.masterData.MUValidity
            ],
            npAuthorization: [
                this.ulipData.rc_permit_valid_upto,
                this.masterData.NPAValidity
            ],
            permit: [
                this.ulipData.rc_permit_valid_upto,
                this.masterData.NPValidity
            ],
            pollution: [
                this.ulipData.rc_pucc_upto,
                this.masterData.polutionValidity
            ]
        };
        const values = dateMap[type] || [];
        return values.find(v => !!v) || null;
    },
    downloadDoc(file, label) {
        if (!file) return;
        const mapping = {
            'NP Authorization': 'Vehicle/National_Permit_Authorization/',
            'NP Part A/B': 'Vehicle/NationalPermit/',
            'Fitness Cert': 'Vehicle/FITNESS/',
            'Insurance': 'Vehicle/INSURANCE/',
            'Tax Receipt': 'Vehicle/MUTAX/',
            'Registration Cert': 'Vehicle/RC/',
            'Pollution Cert': 'Vehicle/pollutionCertificate/',
            'Other Document': 'Vehicle/Other/'
        };
        const folder = mapping[label] || 'Vehicle/Other/';
        const key = `upload_pic/${folder}${file}`;
        this.openSignedDocument(key);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

:deep(.vehicle-premium-modal) {
    font-family: 'Inter', sans-serif;
}

:deep(.vehicle-premium-modal .modal-content) {
    border: none;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.4);
}

:deep(.vehicle-premium-modal .modal-header) {
    padding: 0;
    border-bottom: none;
}

.dashboard-header {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    color: white;
    border-bottom: 4px solid #3b82f6;
    position: relative;
    z-index: 10;
}

.vehicle-avatar {
    width: 64px;
    height: 64px;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(12px);
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #3b82f6;
}

.status-indicator {
    position: absolute;
    bottom: -3px;
    right: -3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid #0f172a;
}
.status-indicator.active { background-color: #10b981; box-shadow: 0 0 10px #10b981; }
.status-indicator.inactive { background-color: #64748b; }

.vehicle-identity h3 {
    letter-spacing: -0.01em;
    line-height: 1;
}

.premium-badge {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 6px;
    font-weight: 700;
}

.header-stats {
    display: flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    padding: 8px 16px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 70px;
}

.stat-group-clickable {
    cursor: pointer;
}

.stat-label {
    font-size: 9px;
    text-transform: uppercase;
    font-weight: 800;
    opacity: 0.5;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
}

.stat-value {
    font-size: 1.1rem;
    font-weight: 800;
}
.stat-value.success { color: #10b981; }
.stat-value.warning { color: #f59e0b; }
.stat-value.info { color: #3b82f6; }

.stat-divider {
    width: 1px;
    height: 24px;
    background: rgba(255, 255, 255, 0.1);
    margin: 0 16px;
}

.premium-close-btn {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0;
}

.premium-close-btn:hover {
    background: #ef4444;
    border-color: #ef4444;
    transform: rotate(90deg);
}

.modal-dashboard-layout {
    display: flex;
    height: 720px;
    background: #f1f5f9;
}

.dashboard-sidebar {
    width: 250px;
    background: white;
    border-right: 1px solid #e2e8f0;
    padding: 24px 16px;
    flex-shrink: 0;
}

.nav-item-premium {
    display: flex;
    align-items: center;
    padding: 14px 18px;
    margin-bottom: 10px;
    border-radius: 14px;
    cursor: pointer;
    color: #475569;
    font-weight: 700;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

.nav-item-premium i {
    width: 20px;
    font-size: 1rem;
    margin-right: 14px;
    opacity: 0.6;
}

.nav-item-premium:hover {
    background: #f8fafc;
    color: #0f172a;
    transform: translateX(4px);
}

.nav-item-premium.active {
    background: #0f172a;
    color: white;
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.2);
}

.nav-item-premium.active i {
    opacity: 1;
    color: #3b82f6;
}

.dashboard-main-content {
    flex-grow: 1;
    overflow-y: auto;
    padding: 32px;
}

:deep(.premium-badge) {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border: none;
    font-weight: 700;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
}

:deep(.dot-badge) {
    font-size: 10px;
    padding: 4px 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 800;
}

.text-xs { font-size: 0.75rem; }

.glass-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01);
}

.card-subtitle-premium {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.05em;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
}

.card-subtitle-premium i {
    color: #3b82f6;
    margin-right: 12px;
}

.info-label {
    display: block;
    font-size: 11px;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    margin-bottom: 6px;
    letter-spacing: 0.2px;
}

.info-value-premium {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
}

.compliance-row {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.compliance-row span:first-child {
    font-size: 0.85rem;
    font-weight: 700;
    color: #475569;
}

.compliance-row .date {
    font-size: 0.9rem;
}

.progress-simple {
    height: 5px;
    background: #f1f5f9;
    border-radius: 3px;
    margin-top: 8px;
    position: relative;
    overflow: hidden;
}

.progress-simple::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: 3px;
    transition: width 0.6s ease;
}

.status-success::after { background: #10b981; width: 100%; }
.status-warning::after { background: #f59e0b; width: 60%; }
.status-danger::after { background: #ef4444; width: 100%; opacity: 0.3; }
.status-none::after { background: #cbd5e1; width: 0; }

.doc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
}

.doc-card {
    display: flex;
    align-items: center;
    padding: 16px;
    background: #f8fafc;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #edf2f7;
}

.doc-card:hover {
    transform: translateY(-3px);
    background: white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    border-color: #3b82f6;
}

.doc-icon {
    width: 44px;
    height: 44px;
    background: #fef2f2;
    color: #ef4444;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    margin-right: 16px;
}

.doc-info {
    display: flex;
    flex-direction: column;
}

.doc-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1e293b;
}

.doc-action {
    font-size: 0.75rem;
    font-weight: 600;
}

.owner-card {
    background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%);
    position: relative;
    overflow: hidden;
}

.stat-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.stat-cell label {
    font-size: 9px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 4px;
    display: block;
}

.stat-cell span {
    font-size: 0.95rem;
    font-weight: 700;
    display: block;
}

.info-item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;
}

.info-item-row:last-child {
    border-bottom: none;
}

.info-item-row label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #64748b;
    margin: 0;
}

.info-item-row span {
    font-size: 0.9rem;
    font-weight: 750;
    color: #1e293b;
}

.premium-table {
    border-collapse: separate;
    border-spacing: 0 10px;
}

.premium-table thead th {
    background: transparent;
    border: none;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 800;
    color: #64748b;
    padding: 12px 24px;
    letter-spacing: 0.5px;
}

.premium-table tbody td {
    background: white;
    border: none;
    padding: 20px 24px;
    vertical-align: middle;
}

.premium-table tbody tr td:first-child { border-radius: 16px 0 0 16px; border-left: 1px solid #e2e8f0; }
.premium-table tbody tr td:last-child { border-radius: 0 16px 16px 0; border-right: 1px solid #e2e8f0; }
.premium-table tbody tr td { border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }

.premium-table tbody tr:hover td {
    background: #f8fafc;
    border-color: #3b82f6;
}

.direction-badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.direction-badge.I { background: #dcfce7; color: #166534; }
.direction-badge.O { background: #e0f2fe; color: #075985; }
.direction-badge i { margin-right: 8px; }

.offence-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.offence-chip {
    background: #fee2e2;
    color: #b91c1c;
    padding: 4px 12px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 800;
    border: 1px solid rgba(185, 28, 28, 0.1);
}

.loader-container {
    height: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: white;
}

.premium-loader {
    width: 60px;
    height: 60px;
    border: 4px solid #f1f5f9;
    border-radius: 50%;
    border-top-color: #3b82f6;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.fade-in {
    animation: fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}

.error-container {
    background: white;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
</style>




// WEBPACK FOOTER //
// VehicleMasterDetailsModal.vue?1b8069ce